import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import {
  Zap,
  BarChart3,
  Target,
  TrendingUp,
  ArrowRight,
  Instagram,
  Youtube,
  Twitter,
  LinkedinIcon,
  Send,
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
  Users,
  LineChart,
  Rocket,
  PhoneCall,
  FileText,
  MessageCircle,
  UserCheck
} from 'lucide-react';

function FadeInSection({ children }: { children: React.ReactNode }) {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
    rootMargin: '10px'
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ 
        duration: 0.3,
        type: "spring",
        stiffness: 260,
        damping: 20
      }}
    >
      {children}
    </motion.div>
  );
}

function App() {
  const navigate = useNavigate();
  const [showLearnMore, setShowLearnMore] = React.useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await fetch('https://formsubmit.co/zambokin65@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          _subject: 'New Contact Form Submission',
          _next: 'https://18cc8c24.zimerson.pages.dev/success'
        })
      });
      
      setFormData({ name: '', email: '', message: '' });
      navigate('/success');
    } catch (error) {
      alert('Failed to send message. Please try again.');
    }
  };

  const toggleLearnMore = () => {
    setShowLearnMore(!showLearnMore);
    if (!showLearnMore) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="fixed w-full bg-black/80 backdrop-blur-md z-50">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <motion.div 
              className="flex items-center space-x-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <Zap className="h-8 w-8 text-blue-500" />
              <span className="text-xl font-bold text-white">Zimerson</span>
            </motion.div>
            <motion.div 
              className="hidden md:flex items-center space-x-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <a href="#home" className="text-gray-200 hover:text-blue-400 transition-colors">Home</a>
              <a href="#services" className="text-gray-200 hover:text-blue-400 transition-colors">Services</a>
              <a href="#how-it-works" className="text-gray-200 hover:text-blue-400 transition-colors">How it Works</a>
              <a href="#contact" className="text-gray-200 hover:text-blue-400 transition-colors">Contact</a>
              <a 
                href="/join"
                className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-2 rounded-full font-semibold 
                         hover:from-blue-700 hover:to-blue-600 transition-colors"
              >
                Join
              </a>
            </motion.div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center z-0"
          style={{
            backgroundImage: 'url("/giphy.gif")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            loading: 'lazy'
          }}
        />
        <div className="absolute inset-0 bg-black/60 z-10" />
        
        <div className="container mx-auto px-6 py-20 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="responsive-title title-gradient mb-16 leading-tight">
              Transform Your Brand with Influencer Marketing
            </h1>
            <motion.button 
              onClick={toggleLearnMore}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-10 py-5 rounded-full text-responsive font-bold
                       flex items-center space-x-3 hover:from-blue-700 hover:to-blue-600 transition-all mx-auto"
            >
              <span>Learn More</span>
              <ArrowRight className="h-6 w-6" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Learn More Modal */}
      {showLearnMore && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 backdrop-blur-lg z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-gradient-to-br from-blue-950/30 to-black/30 p-8 rounded-2xl max-w-3xl relative backdrop-blur-xl"
          >
            <button
              onClick={toggleLearnMore}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              ✕
            </button>
            <h2 className="text-responsive-xl font-bold text-blue-400 mb-6">Welcome to Zimerson</h2>
            <div className="space-y-6 text-responsive text-gray-300">
              <p className="leading-relaxed">
                Zimerson Media is an International Talent Management and Influencer Marketing Agency, based in the United States. We are dedicated to working with the world's biggest social media influencers and brands to drive highly impactful campaigns to their audiences.
              </p>
              <p className="leading-relaxed">
                We specialise in helping content creators turn their social influence and passion into long-lasting careers and connecting brands to the most powerful form of marketing: influence. We work with them to highlight their products and services to millions across the world.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Services Section */}
      <section id="services" className="py-20 bg-gradient-to-b from-black via-blue-950/10 to-black">
        <div className="container mx-auto px-6">
          <FadeInSection>
            <h2 className="responsive-section-heading text-center mb-16 text-white">Our Core Services</h2>
            <div className="grid md:grid-cols-3 gap-12">
              {[
                {
                  icon: Target,
                  title: "Influencer Discovery",
                  description: "Find the perfect content creators who align with your brand values and target audience demographics."
                },
                {
                  icon: TrendingUp,
                  title: "Campaign Management",
                  description: "End-to-end campaign execution, from strategy development to performance tracking and optimization."
                },
                {
                  icon: BarChart3,
                  title: "Analytics & Reporting",
                  description: "Comprehensive performance metrics and ROI analysis to measure campaign success."
                }
              ].map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ 
                    duration: 0.5,
                    delay: index * 0.1,
                    type: "spring",
                    stiffness: 100,
                    damping: 15
                  }}
                  whileHover={{ scale: 1.01 }}
                  className="service-card p-8 rounded-lg bg-gradient-to-br from-black/90 to-blue-950/30 border border-blue-500/5"
                >
                  <service.icon className="h-12 w-12 text-blue-500 mb-6" />
                  <h3 className="text-responsive-lg font-semibold mb-4 text-white">{service.title}</h3>
                  <p className="text-responsive text-gray-300">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-20 bg-gradient-to-b from-black via-blue-950/10 to-black">
        <div className="container mx-auto px-6">
          <FadeInSection>
            <h2 className="responsive-section-heading text-center mb-16 text-white">How It Works</h2>
            <div className="grid md:grid-cols-4 gap-8">
              {[
                {
                  step: "1",
                  icon: PhoneCall,
                  title: "Discovery Call",
                  description: "We learn about your brand, goals, and target audience"
                },
                {
                  step: "2",
                  icon: FileText,
                  title: "Strategy Development",
                  description: "Custom campaign strategy aligned with your objectives"
                },
                {
                  step: "3",
                  icon: UserCheck,
                  title: "Influencer Selection",
                  description: "Carefully curated creators for your campaign"
                },
                {
                  step: "4",
                  icon: Rocket,
                  title: "Launch & Optimize",
                  description: "Campaign execution with real-time optimization"
                }
              ].map((item, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  className="text-center"
                >
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    className="w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-6"
                  >
                    <item.icon className="h-10 w-10" />
                  </motion.div>
                  <h3 className="text-responsive-lg font-semibold mb-3 text-white">{item.title}</h3>
                  <p className="text-responsive text-gray-300">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 bg-gradient-to-b from-black via-blue-950/10 to-black">
        <div className="container mx-auto px-6">
          <FadeInSection>
            <h2 className="responsive-section-heading text-center mb-16 text-white">Our Impact</h2>
            <div className="grid md:grid-cols-3 gap-12">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                className="text-center p-8 rounded-lg service-card"
              >
                <Target className="h-16 w-16 text-blue-500 mx-auto mb-6" />
                <h3 className="text-responsive-lg font-semibold mb-4 text-white">Tailored Campaign Strategies</h3>
                <p className="text-responsive text-gray-300">We create influencer marketing campaigns designed specifically for your brand's needs.</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-center p-8 rounded-lg service-card"
              >
                <Users className="h-16 w-16 text-blue-500 mx-auto mb-6" />
                <h3 className="text-responsive-lg font-semibold mb-4 text-white">Growing Audience Reach</h3>
                <p className="text-responsive text-gray-300">We connect brands with the right influencers to amplify your message.</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-center p-8 rounded-lg service-card"
              >
                <LineChart className="h-16 w-16 text-blue-500 mx-auto mb-6" />
                <h3 className="text-responsive-lg font-semibold mb-4 text-white">Commitment to ROI</h3>
                <p className="text-responsive text-gray-300">Our focus is on delivering results that drive growth and meaningful connections.</p>
              </motion.div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-b from-black via-blue-950/10 to-black">
        <div className="container mx-auto px-6">
          <FadeInSection>
            <h2 className="responsive-section-heading text-center mb-16 text-white">What Our Clients Say</h2>
            <div className="grid md:grid-cols-3 gap-12">
              {[
                {
                  quote: "Zimerson transformed our social media presence. The ROI exceeded our expectations.",
                  author: "Sarah Johnson",
                  role: "Marketing Director, TechCorp"
                },
                {
                  quote: "Their strategic approach to influencer selection helped us reach our ideal audience effectively.",
                  author: "Michael Chen",
                  role: "CEO, StyleBrand"
                },
                {
                  quote: "Professional team, outstanding results. They truly understand influencer marketing.",
                  author: "Emma Davis",
                  role: "Brand Manager, FitLife"
                }
              ].map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  whileHover={{ scale: 1.02 }}
                  className="testimonial-gradient p-8 rounded-lg shadow-lg"
                >
                  <CheckCircle2 className="h-8 w-8 text-blue-500 mb-6" />
                  <p className="text-responsive text-white mb-6 leading-relaxed">{testimonial.quote}</p>
                  <div className="text-responsive font-semibold text-white">{testimonial.author}</div>
                  <div className="text-responsive-sm text-blue-400">{testimonial.role}</div>
                </motion.div>
              ))}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-black via-blue-950/10 to-black">
        <div className="container mx-auto px-6">
          <FadeInSection>
            <div className="max-w-4xl mx-auto">
              <h2 className="responsive-section-heading text-center mb-16 text-white">Get in Touch</h2>
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-8">Contact Information</h2>
                  <div className="space-y-6">
                    <div className="flex items-center space-x-4">
                      <Mail className="h-6 w-6 text-blue-500" />
                      <a href="mailto:contact@zimerson.com" className="text-gray-300 hover:text-blue-400 transition-colors">
                        contact@zimerson.com
                      </a>
                    </div>
                    <div className="flex items-center space-x-4">
                      <MapPin className="h-6 w-6 text-blue-500" />
                      <span className="text-gray-300">United States</span>
                    </div>
                  </div>
                </div>
                <div>
                  <form 
                    action="https://api.web3forms.com/submit"
                    method="POST"
                    className="space-y-6"
                  >
                    <input type="hidden" name="access_key" value="94946f6f-3d9c-492c-bdeb-ea0b3b02e064" />
                    <input type="hidden" name="subject" value="New Contact Form Submission" />
                    <input type="hidden" name="from_name" value="Zimerson Website" />
                    <input type="hidden" name="redirect" value="/success" />
                    
                    <motion.input
                      whileFocus={{ scale: 1.01 }}
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      className="w-full px-4 py-3 rounded-lg newsletter-input text-responsive text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      required
                    />
                    <motion.input
                      whileFocus={{ scale: 1.01 }}
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      className="w-full px-4 py-3 rounded-lg newsletter-input text-responsive text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      required
                    />
                    <motion.textarea
                      whileFocus={{ scale: 1.01 }}
                      name="message"
                      placeholder="Your Message"
                      rows={4}
                      className="w-full px-4 py-3 rounded-lg newsletter-input text-responsive text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      required
                    ></motion.textarea>
                    <motion.button 
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-3 rounded-lg text-responsive font-semibold hover:from-blue-700 hover:to-blue-600 transition-colors"
                    >
                      Send Message
                    </motion.button>
                  </form>
                </div>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-blue-950/10 to-black text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center space-x-2 mb-4">
                <Zap className="h-6 w-6 text-blue-500" />
                <span className="text-responsive font-bold">Zimerson</span>
              </div>
              <p className="text-responsive text-gray-400">Connecting brands with authentic voices in the digital age.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h3 className="text-responsive font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#services" className="text-responsive hover:text-blue-400 transition-colors">Services</a></li>
                <li><a href="#how-it-works" className="text-responsive hover:text-blue-400 transition-colors">How it Works</a></li>
                <li><a href="#contact" className="text-responsive hover:text-blue-400 transition-colors">Contact</a></li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <h3 className="text-responsive font-semibold mb-4">Follow Us</h3>
              <div className="flex space-x-4">
                <motion.a whileHover={{ scale: 1.2 }} href="#" className="text-gray-400 hover:text-blue-400 transition-colors">
                  <Instagram className="h-6 w-6" />
                </motion.a>
                <motion.a whileHover={{ scale: 1.2 }} href="#" className="text-gray-400 hover:text-blue-400 transition-colors">
                  <Twitter className="h-6 w-6" />
                </motion.a>
                <motion.a whileHover={{ scale: 1.2 }} href="#" className="text-gray-400 hover:text-blue-400 transition-colors">
                  <LinkedinIcon className="h-6 w-6" />
                </motion.a>
                <motion.a whileHover={{ scale: 1.2 }} href="#" className="text-gray-400 hover:text-blue-400 transition-colors">
                  <Youtube className="h-6 w-6" />
                </motion.a>
              </div>
            </motion.div>
          </div>
          <div className="mt-12 pt-8 text-center text-gray-400">
            <p className="text-responsive">© 2025 Zimerson. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;

// Replace the Join button section with this form
<form 
  action="https://formsubmit.co/zambokin65@gmail.com" 
  method="POST"
  className="hidden md:flex items-center space-x-4"
>
  <input type="hidden" name="_subject" value="New Join Request" />
  <input type="hidden" name="_next" value="https://18cc8c24.zimerson.pages.dev/success" />
  <input type="hidden" name="_captcha" value="false" />
  <input
    type="text"
    name="name"
    placeholder="Name"
    className="px-4 py-2 rounded-full newsletter-input text-white"
    required
  />
  <input
    type="email"
    name="email"
    placeholder="Email"
    className="px-4 py-2 rounded-full newsletter-input text-white"
    required
  />
  <button 
    type="submit"
    className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-2 rounded-full font-semibold 
             hover:from-blue-700 hover:to-blue-600 transition-colors"
  >
    Join
  </button>
</form>